// Long enough to read the longest result message, short enough not to linger.
export const NOTICE_DURATION = 7000;
// The offer never interrupts arrival: it opens after this long on the page,
// or once the shopper reaches this scroll depth (Clarity's definition), at most
// once per day per browser. Tapping an offer opener always works.
export const AUTO_OPEN_DELAY = 20000;
export const AUTO_OPEN_DEPTH = 0.5;
export const AUTO_OPEN_KEY = 'marte-offers:auto-opened';

export function activeCodes(config, now = Date.now()) {
  const codes = [];
  if (now < Date.parse(config.welcomeEnd)) codes.push('WELCOME10');
  if (now >= Date.parse(config.start) && now < Date.parse(config.end)) codes.push(config.shippingCode);
  return codes;
}

// Native automatic discounts need no code; the schedule mirrors their admin dates.
export function automaticActive(config, now = Date.now()) {
  return Boolean(config.automaticStart && config.automaticEnd) &&
    now >= Date.parse(config.automaticStart) && now < Date.parse(config.automaticEnd);
}

// Store calendar day, so repeat ad taps on the same day do not reopen the offer.
export function dayKey(now = Date.now()) {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Monterrey', year: 'numeric', month: '2-digit', day: '2-digit' }).format(now);
}

export function scrollDepth(scrollY, viewport, height) {
  return height > 0 ? (scrollY + viewport) / height : 0;
}

// Remaining time to the real end, rounded down; matches the Liquid rendering.
export function timeLeft(ms) {
  const seconds = Math.max(0, Math.floor(ms / 1000));
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.max(1, Math.floor((seconds % 3600) / 60));
  if (days > 0) return `${days} d ${hours} h`;
  if (hours > 0) return `${hours} h ${minutes} min`;
  return `${minutes} min`;
}

export function mergeCodes(existing, offers) {
  return [...new Set([...existing.map(({ code }) => code.toUpperCase()), ...offers])];
}

export function codesStored(cart, codes) {
  return codes.length > 0 && codes.every((code) =>
    (cart.discount_codes || []).some((entry) => entry.code.toUpperCase() === code));
}

// English remains the original fallback, including for already-cached English markup.
// Spanish messages are serialized by Liquid from marte-copy, not duplicated here.
export const englishMessages = Object.freeze({
  unconfirmed: 'We could not confirm every code. Check your cart or enter the codes at checkout. Eligibility and final savings are confirmed there.',
  empty: 'Codes saved to your empty cart. Add a piece to check eligibility. No savings have been applied yet.',
  ineligible_prefix: 'WELCOME10 is not eligible for this cart right now. It may be expired or have reached its usage limit. ',
  shipping_saved: 'The shipping code is saved for checkout to check your Mexico address and standard rate.',
  check_eligibility: 'Check eligibility at checkout.',
  eligible_prefix: 'WELCOME10 is eligible for this cart. ',
  shipping_confirmed: 'Shipping code saved. Free shipping is confirmed at checkout for an eligible Mexico address and standard rate.',
  review_savings: 'Review the final savings at checkout.',
  copied: '__CODE__ copied. Use Apply & keep browsing to save it to your cart.',
  copy_failed: 'Copy __CODE__ manually, or use Apply & keep browsing.',
  combined_heading: '10% off + free shipping',
  shipping_benefit: 'Free standard shipping',
  welcome_heading: 'Welcome 10% off',
  ended_heading: 'This offer has ended',
  ended: 'This promotion has ended. You can continue shopping without it.',
  saving: 'Saving codes…',
  checking: 'Checking your cart and saving the eligible offers…',
  refresh_suffix: ' Refresh the cart to see its latest totals.',
  update_failed: 'We could not confirm the update. Check your cart or enter the codes at checkout. No savings are confirmed yet; you can try again.',
  apply: 'Apply & keep browsing',
  view_suffix: ': view offers',
  limited_suffix: ' for a limited time',
  continue: 'Keep browsing',
  left: '__LEFT__ left',
});

export function applicationState(cart, codes, shippingCode) {
  if (!codesStored(cart, codes)) return 'unconfirmed';
  if (!cart.item_count) return 'empty';
  const welcome = (cart.discount_codes || []).find((entry) => entry.code.toUpperCase() === 'WELCOME10');
  const shipping = codes.includes(shippingCode);
  if (codes.includes('WELCOME10') && !welcome?.applicable) return shipping ? 'ineligibleShipping' : 'ineligible';
  if (welcome?.applicable && codes.includes('WELCOME10')) return shipping ? 'eligibleShipping' : 'eligible';
  return shipping ? 'shipping' : 'review';
}

export function resultMessage(state, copy = englishMessages) {
  switch (state) {
    case 'unconfirmed': return copy.unconfirmed;
    case 'empty': return copy.empty;
    case 'ineligibleShipping': return copy.ineligible_prefix + copy.shipping_saved;
    case 'ineligible': return copy.ineligible_prefix + copy.check_eligibility;
    case 'eligibleShipping': return copy.eligible_prefix + copy.shipping_confirmed;
    case 'eligible': return copy.eligible_prefix + copy.review_savings;
    case 'shipping': return copy.shipping_confirmed;
    case 'review': return copy.review_savings;
    default: return '';
  }
}

export function applicationMessage(cart, codes, shippingCode, copy = englishMessages) {
  return resultMessage(applicationState(cart, codes, shippingCode), copy);
}

export function savedResultState(value) {
  if (typeof value !== 'string' || value.length > 1024) return null;
  try {
    const data = JSON.parse(value);
    if (data?.version === 1 && typeof data.state === 'string' && resultMessage(data.state)) return data.state;
  } catch { /* Recognize only exact legacy messages, never display arbitrary stored text. */ }
  return ['unconfirmed', 'empty', 'ineligibleShipping', 'ineligible', 'eligibleShipping', 'eligible', 'shipping', 'review']
    .find((state) => resultMessage(state) === value) || null;
}

if (typeof customElements !== 'undefined' && !customElements.get('marte-offer')) {
  customElements.define('marte-offer', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.dialog = this.querySelector('dialog');
      this.trigger = this.querySelector('.marte-offer-trigger');
      this.applyButton = this.querySelector('[data-offer-apply]');
      this.status = this.querySelector('[data-offer-status]');
      this.notice = this.querySelector('[data-offer-notice]');
      // Older cached markup used an in-page aside; style it as the same toast.
      this.notice.classList.add('marte-offer-toast');
      this.terms = this.querySelector('[data-offer-terms]');
      this.config = { ...this.dataset };
      this.copy = englishMessages;
      if (this.dataset.locale === 'es') {
        const translated = JSON.parse(this.querySelector('[data-offer-messages]').textContent);
        if (!Object.keys(englishMessages).every((key) => typeof translated[key] === 'string' && translated[key])) {
          throw new Error('Incomplete Spanish offer messages');
        }
        this.copy = translated;
      }
      const serverNow = Number(this.dataset.now);
      this.clockOrigin = Number.isFinite(serverNow) && serverNow > 0 ? serverNow : Date.now();
      this.performanceOrigin = performance.now();
      this.root = this.dataset.root.replace(/\/?$/, '/');
      this.memoryKey = `marte-offers:${this.config.start || 'welcome10'}:seen`;
      this.showSavedNotice();
      this.refresh();
      this.controller = new AbortController();
      this.resizeObserver = new ResizeObserver(() => this.syncOverflow());
      this.resizeObserver.observe(this.querySelector('.marte-offer-dialog__body'));
      const options = { signal: this.controller.signal };
      document.addEventListener('click', (event) => {
        const opener = event.target.closest('[data-marte-offer-open]');
        if (!opener) return;
        event.preventDefault();
        this.open(opener);
      }, options);
      this.querySelector('[data-offer-close]').addEventListener('click', () => this.dialog.close(), options);
      this.terms.addEventListener('toggle', () => this.syncOverflow(), options);
      this.notice.querySelector('button').addEventListener('click', () => this.hideNotice(), options);
      // Pause the auto-dismiss while the shopper reads or tabs to the toast.
      const hold = () => { this.noticeHeld = true; this.pauseNotice(); };
      const release = () => {
        this.noticeHeld = this.notice.matches(':hover') || this.notice.contains(document.activeElement);
        if (!this.noticeHeld) this.resumeNotice();
      };
      this.notice.addEventListener('pointerenter', hold, options);
      this.notice.addEventListener('pointerleave', release, options);
      this.notice.addEventListener('focusin', hold, options);
      this.notice.addEventListener('focusout', () => setTimeout(release), options);
      this.dialog.addEventListener('close', () => {
        this.remember();
        this.trigger.setAttribute('aria-expanded', 'false');
        let target = this.opener?.isConnected ? this.opener :
          this.returnContext?.querySelector('[data-marte-offer-open]') || this.trigger;
        if (target === this.trigger && this.trigger.hasAttribute('data-covered')) {
          target = document.querySelector('main [data-offer-line]:not([hidden])') || target;
        }
        if (!target.hidden) target.focus({ preventScroll: true });
      }, options);
      this.dialog.addEventListener('keydown', (event) => {
        // Atelier's drawer also listens on document. Only the top modal should close.
        if (event.key === 'Escape' && !event.isComposing) {
          event.preventDefault();
          event.stopPropagation();
          this.dialog.close();
          return;
        }
        if (event.key !== 'Tab') return;
        const controls = [...this.dialog.querySelectorAll('button:not(:disabled), a[href], [tabindex="0"]')]
          .filter((node) => !node.closest('[hidden]') && node.getClientRects().length);
        const first = controls[0];
        const last = controls.at(-1);
        if (event.shiftKey && (document.activeElement === first || document.activeElement === this.dialog)) {
          event.preventDefault();
          last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first?.focus();
        }
      }, options);
      this.dialog.addEventListener('click', (event) => {
        if (event.target !== this.dialog) return;
        const rect = this.dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.dialog.close();
      }, options);
      this.applyButton.addEventListener('click', () => this.apply(), options);
      this.querySelectorAll('[data-copy-code]').forEach((button) => {
        button.addEventListener('click', async () => {
          try {
            await navigator.clipboard.writeText(button.dataset.copyCode);
            this.status.textContent = this.copy.copied.replaceAll('__CODE__', () => button.dataset.copyCode);
          } catch {
            this.status.textContent = this.copy.copy_failed.replaceAll('__CODE__', () => button.dataset.copyCode);
          }
        }, options);
      });
      document.addEventListener('visibilitychange', () => this.refresh(), options);
      // The product offer line already opens the dialog. Hide the floating
      // trigger while that line is on screen so it never covers it on short phones.
      const covering = new Set();
      this.intersection = new IntersectionObserver((entries) => {
        entries.forEach((entry) => (entry.isIntersecting ? covering.add(entry.target) : covering.delete(entry.target)));
        this.trigger.toggleAttribute('data-covered', covering.size > 0);
      });
      document.querySelectorAll('main [data-marte-offer-product]').forEach((node) => this.intersection.observe(node));
      // Keep "ends in" lines current without ever restarting them.
      this.ticker = setInterval(() => this.updateProgress(), 30000);
      this.scheduleAutoOpen();
    }

    now() {
      return this.clockOrigin + performance.now() - this.performanceOrigin;
    }

    get active() {
      return this.automatic || this.codes.length > 0;
    }

    // Never on arrival: after AUTO_OPEN_DELAY on the page or AUTO_OPEN_DEPTH of
    // scrolling, whichever comes first, once per day. Never over another dialog
    // or once the cart has a piece.
    scheduleAutoOpen() {
      if (this.dataset.designMode === 'true' || this.openedToday()) return;
      const { signal } = this.controller;
      let done = false;
      const fire = async () => {
        if (done) return;
        if (document.hidden) {
          document.addEventListener('visibilitychange', fire, { once: true, signal });
          return;
        }
        done = true;
        clearTimeout(this.autoTimer);
        this.refresh();
        const blocked = () => !this.active || this.openedToday() || this.dialog.open || document.querySelector('dialog[open], shopify-chat[open]');
        if (blocked()) return;
        // A shopper who already added a piece sees the offer in the cart; do not interrupt.
        try {
          const cart = await (await fetch(this.root + 'cart.js', { headers: { Accept: 'application/json' } })).json();
          if (cart.item_count > 0) return;
        } catch { /* Unknown cart: fall through to the usual checks. */ }
        if (blocked()) return;
        this.open(document.querySelector('main .cart-page__summary [data-marte-offer-open]') || this.trigger);
      };
      this.autoTimer = setTimeout(fire, AUTO_OPEN_DELAY);
      addEventListener('scroll', () => {
        const root = document.documentElement;
        if (scrollY > 0 && scrollDepth(scrollY, innerHeight, root.scrollHeight) >= AUTO_OPEN_DEPTH) fire();
      }, { passive: true, signal });
    }

    storages() {
      const stores = [];
      try { stores.push(localStorage); } catch { /* Blocked storage falls back below. */ }
      try { stores.push(sessionStorage); } catch { /* Memory fallback still prevents repeats on this page. */ }
      return stores;
    }

    openedToday() {
      const today = dayKey(this.now());
      if (this.openedOn === today) return true;
      let seen = false;
      try { seen = sessionStorage.getItem(this.memoryKey) === '1'; } catch { /* Storage is optional. */ }
      return seen || this.storages().some((store) => {
        try { return store.getItem(AUTO_OPEN_KEY) === today; } catch { return false; }
      });
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.resizeObserver?.disconnect();
      this.intersection?.disconnect();
      clearTimeout(this.timer);
      clearTimeout(this.noticeTimer);
      clearTimeout(this.autoTimer);
      clearInterval(this.ticker);
      this.ready = false;
    }

    remember() {
      try { sessionStorage.setItem(this.memoryKey, '1'); } catch { /* No marketing or storage consent required. */ }
      this.openedOn = dayKey(this.now());
      this.storages().forEach((store) => {
        try { store.setItem(AUTO_OPEN_KEY, this.openedOn); } catch { /* Quota or privacy mode. */ }
      });
    }

    // Hide the unlocked summaries at the real end and keep their remaining time current.
    updateProgress(now = this.now()) {
      const running = automaticActive(this.config, now);
      document.querySelectorAll('[data-offer-progress]').forEach((node) => {
        const end = Date.parse(node.dataset.offerEnd);
        if (!running || !Number.isFinite(end) || now >= end) {
          node.hidden = true;
          return;
        }
        const countdown = node.querySelector('[data-offer-countdown]');
        if (countdown) countdown.textContent = this.copy.left.replace('__LEFT__', timeLeft(end - now));
      });
    }

    showSavedNotice() {
      let state;
      try {
        state = savedResultState(sessionStorage.getItem('marte-offers:result'));
        sessionStorage.removeItem('marte-offers:result');
      } catch { /* Navigation still works without optional storage. */ }
      if (state) this.showNotice(state);
    }

    // A small floating toast reports the confirmed cart result without moving
    // focus. It stacks just above the offer trigger, which already clears the
    // header and the chat bubble, and dismisses itself after a readable interval.
    showNotice(state) {
      const message = resultMessage(state, this.copy);
      if (!message) return;
      const text = this.notice.querySelector('p');
      const trigger = this.trigger.getBoundingClientRect();
      const style = getComputedStyle(this.trigger);
      this.notice.style.removeProperty('--marte-offer-toast-bottom');
      this.notice.style.removeProperty('--marte-offer-toast-top');
      if (!this.trigger.hidden && trigger.height && style.visibility === 'visible') {
        this.notice.style.setProperty('--marte-offer-toast-bottom', `${Math.round(innerHeight - trigger.top + 12)}px`);
      }
      // Phones show it just below the visible header, clear of the hero CTA, trigger and chat.
      const header = Math.max(0, ...[...document.querySelectorAll('header-component, body > header, #header-group')]
        .map((node) => node.getBoundingClientRect().bottom).filter((bottom) => bottom > 0 && bottom < innerHeight / 2));
      if (header) this.notice.style.setProperty('--marte-offer-toast-top', `${Math.round(header + 8)}px`);
      clearTimeout(this.noticeTimer);
      this.notice.removeAttribute('data-leaving');
      this.notice.hidden = false;
      // Replace the text after the live region is rendered so it is announced.
      text.textContent = '';
      requestAnimationFrame(() => requestAnimationFrame(() => {
        text.textContent = message;
        this.notice.setAttribute('data-visible', '');
      }));
      this.noticeRemaining = NOTICE_DURATION;
      if (!this.noticeHeld) this.resumeNotice();
    }

    pauseNotice() {
      if (!this.noticeTimer) return;
      clearTimeout(this.noticeTimer);
      this.noticeTimer = null;
      this.noticeRemaining = Math.max(2000, this.noticeRemaining - (performance.now() - this.noticeStarted));
    }

    resumeNotice() {
      if (this.notice.hidden || this.notice.hasAttribute('data-leaving') || this.noticeTimer) return;
      this.noticeStarted = performance.now();
      this.noticeTimer = setTimeout(() => this.hideNotice(), this.noticeRemaining);
    }

    hideNotice() {
      clearTimeout(this.noticeTimer);
      this.noticeTimer = null;
      if (this.notice.hidden || this.notice.hasAttribute('data-leaving')) return;
      if (this.notice.contains(document.activeElement)) {
        const target = [this.opener, this.trigger, document.querySelector('main [data-offer-line]')]
          .find((node) => node?.isConnected && node.getClientRects().length && getComputedStyle(node).visibility === 'visible');
        target?.focus({ preventScroll: true });
      }
      this.noticeHeld = false;
      this.notice.removeAttribute('data-visible');
      this.notice.setAttribute('data-leaving', '');
      const done = () => {
        if (!this.notice.hasAttribute('data-leaving')) return;
        this.notice.removeAttribute('data-leaving');
        this.notice.hidden = true;
        this.notice.querySelector('p').textContent = '';
      };
      if (matchMedia('(prefers-reduced-motion: reduce)').matches) done();
      else setTimeout(done, 220);
    }

    refresh() {
      const now = this.now();
      this.codes = activeCodes(this.config, now);
      this.automatic = automaticActive(this.config, now);
      clearTimeout(this.timer);
      const nextBoundary = [this.config.start, this.config.end, this.config.welcomeEnd, this.config.automaticEnd]
        .map(Date.parse).filter((time) => Number.isFinite(time) && time > now).sort((a, b) => a - b)[0];
      if (nextBoundary) this.timer = setTimeout(() => this.refresh(), Math.min(nextBoundary - now + 20, 2147483647));
      const shipping = this.automatic || this.codes.includes(this.config.shippingCode);
      const welcome = this.automatic || this.codes.includes('WELCOME10');
      const heading = welcome && shipping ? this.copy.combined_heading : shipping ? this.copy.shipping_benefit : welcome ? this.copy.welcome_heading : this.copy.ended_heading;
      const toggle = (selector, hidden) => { const node = this.querySelector(selector); if (node) node.hidden = hidden; };
      this.querySelectorAll('[data-offer-heading]').forEach((node) => { node.textContent = heading; });
      toggle('[data-shipping-benefit]', !shipping);
      document.querySelectorAll('[data-marte-offer-policy]').forEach((node) => { node.hidden = !shipping; });
      toggle('[data-welcome-benefit]', !welcome);
      toggle('[data-offer-combination]', !(shipping && welcome));
      this.trigger.hidden = !this.active;
      this.applyButton.disabled = this.busy || !this.active;
      if (!this.active) this.status.textContent = this.copy.ended;
      const expiry = this.querySelector('[data-offer-expiry]');
      if (shipping && expiry) {
        expiry.textContent = new Intl.DateTimeFormat(this.dataset.locale === 'es' ? 'es-MX' : 'en-US', {
          timeZone: 'America/Monterrey', month: 'long', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
        }).format(new Date(this.config.end)) + ' CST (UTC-06:00)';
      }
      this.updateProgress(now);
      document.querySelectorAll('[data-marte-offer-inline] [data-marte-offer-open]').forEach((node) => {
        node.hidden = !this.active;
        node.textContent = heading + this.copy.view_suffix;
      });
      document.querySelectorAll('[data-offer-line]').forEach((node) => {
        node.hidden = !this.active;
        // The server renders the real discounted price for the automatic offer.
        if (!(this.automatic && node.hasAttribute('data-offer-auto'))) node.textContent = heading + this.copy.limited_suffix;
      });
    }

    syncOverflow() {
      const body = this.querySelector('.marte-offer-dialog__body');
      this.dialog.toggleAttribute('data-scrollable', this.dialog.open && body.scrollHeight > body.clientHeight + 1);
    }

    open(opener) {
      this.refresh();
      this.opener = opener;
      this.returnContext = opener?.closest('dialog');
      // The product offer line promises full terms one tap away; other openers stay short.
      this.terms.open = Boolean(opener?.hasAttribute('data-offer-show-terms'));
      if (!this.dialog.open) this.dialog.showModal();
      this.syncOverflow();
      this.trigger.setAttribute('aria-expanded', 'true');
      this.remember();
    }

    async apply() {
      if (this.busy) return;
      this.refresh();
      // Automatic discounts apply at checkout on their own: never save codes for them.
      if (this.automatic) {
        this.dialog.close();
        return;
      }
      const codes = [...this.codes];
      if (!codes.length) return;
      this.busy = true;
      this.applyButton.disabled = true;
      this.applyButton.textContent = this.copy.saving;
      this.status.textContent = this.copy.checking;
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 15000);
      try {
        const previous = await fetch(this.root + 'cart.js', { signal: controller.signal, headers: { Accept: 'application/json' } });
        if (!previous.ok) throw new Error('Cart unavailable');
        const cart = await previous.json();
        const discount = mergeCodes(cart.discount_codes || [], codes).join(',');
        const response = await fetch(this.root + 'cart/update.js', {
          method: 'POST', signal: controller.signal,
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ discount }),
        });
        if (!response.ok) throw new Error('Discount update unavailable');
        const updated = await response.json();
        this.status.textContent = applicationMessage(updated, codes, this.config.shippingCode, this.copy);
        // Atelier's existing external-update handler refreshes cart drawer and full cart.
        try {
          const { CartDiscountUpdateEvent } = await import('@shopify/events');
          this.dispatchEvent(new CartDiscountUpdateEvent({
            discountCodes: (updated.discount_codes || []).map(({ code }) => ({ code })),
            promise: Promise.resolve({ cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(updated) }),
          }));
        } catch {
          this.status.textContent += this.copy.refresh_suffix;
        }
        if (codesStored(updated, codes)) {
          // Keep the shopper on the page they came for. Focus returns to the opener;
          // the toast reports the actual cart result through its live region.
          this.dialog.close();
          this.showNotice(applicationState(updated, codes, this.config.shippingCode));
        }
      } catch {
        this.status.textContent = this.copy.update_failed;
      } finally {
        clearTimeout(timeout);
        this.busy = false;
        this.applyButton.textContent = this.copy.apply;
        this.refresh();
      }
    }
  });
}
